/**
 * Sticky Engage filter + count strip for shadow profile discoverability.
 */

import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import {
  SHADOW_PROFILE_FILTER_ALL,
  SHADOW_PROFILE_FILTER_HAS_CONFLICTS,
  SHADOW_PROFILE_FILTER_HAS_PROPOSED,
  SHADOW_PROFILE_FILTER_HAS_SHADOW,
} from '../../utils/beer/beerProfileComparison';

/**
 * @param {{
 *   filter: string,
 *   onFilterChange: (event: unknown, nextFilter: string | null) => void,
 *   counts: { shadowCount: number, proposedDiffCount: number, conflictCount: number },
 *   loading?: boolean,
 *   error?: string | null,
 * }} props
 */
function BeerProfileShadowDiscoverabilityBar({
  filter,
  onFilterChange,
  counts,
  loading,
  error,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 0.75,
        width: '100%',
      }}
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={filter}
        onChange={onFilterChange}
        aria-label="Shadow profile filter"
        sx={{ flexWrap: 'wrap' }}
      >
        <ToggleButton value={SHADOW_PROFILE_FILTER_ALL}>All</ToggleButton>
        <ToggleButton value={SHADOW_PROFILE_FILTER_HAS_SHADOW}>Has shadow</ToggleButton>
        <ToggleButton value={SHADOW_PROFILE_FILTER_HAS_PROPOSED}>Has diffs</ToggleButton>
        <ToggleButton value={SHADOW_PROFILE_FILTER_HAS_CONFLICTS}>Has conflicts</ToggleButton>
      </ToggleButtonGroup>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {loading ? (
          <CircularProgress size={14} aria-label="Loading shadow summaries" />
        ) : null}
        <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
          {`${counts.shadowCount} shadow · ${counts.proposedDiffCount} with diffs · ${counts.conflictCount} conflict${counts.conflictCount === 1 ? '' : 's'}`}
        </Typography>
        {error ? (
          <Typography variant="caption" color="error" component="p" sx={{ m: 0 }}>
            {error}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

BeerProfileShadowDiscoverabilityBar.propTypes = {
  filter: PropTypes.string.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  counts: PropTypes.shape({
    shadowCount: PropTypes.number.isRequired,
    proposedDiffCount: PropTypes.number.isRequired,
    conflictCount: PropTypes.number.isRequired,
  }).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

BeerProfileShadowDiscoverabilityBar.defaultProps = {
  loading: false,
  error: null,
};

export default BeerProfileShadowDiscoverabilityBar;
